import React, { Children, cloneElement, Component, ReactElement } from 'react';

import { SizeMe } from 'react-sizeme';

import { ColumnType, DataTableAPI, SortOrder } from '@philips-emr/tws-components-api';
import { Expression } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent, CollapseEvent, PageEvent, RenderEvent, SortEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, difference, isDefined, isFunction, isMobile } from '@philips-emr/tws-utils';

import { Loader, NotFound } from '..';

import { DataTableColumn, DataTableMultiSelectColumn, DataTableRow } from './components';
import { DataTableColumnProps } from './components/data-table-column';

import './data-table.scss';

interface ColumnNode extends ReactElement {
  props: Partial<DataTableColumnProps>;
}

export interface ColumnGroups {
  barColumns: ColumnNode[];
  defaultColumns: ColumnNode[];
}

export interface ColumnGroupsProps {
  barColumns: Partial<DataTableColumnProps>[];
  defaultColumns: Partial<DataTableColumnProps>[];
}

interface DataTableProps {
  children: ReactElement[];
  className?: string;
  data: Record<string, any>[];
  emptyResultSubtitleExpression?: string;
  emptyResultTitleExpression?: string;
  hiddenColumns?: string[];
  initialExpanded?: boolean;
  keyField: string;
  loading?: boolean;
  multiselect?: boolean;
  onClick?: (event: ClickEvent) => void;
  onCollapse?: (event: CollapseEvent) => void;
  onContextMenuClick?: (event: ClickEvent) => void;
  onDoubleClick?: (event: ClickEvent) => void;
  onDynamicContentRender?: (event: RenderEvent) => void;
  onNextPage?: (event: PageEvent) => void;
  onPrevPage?: (event: PageEvent) => void;
  onSort?: (event: SortEvent) => void;
  showHorizontalBorder?: boolean;
  showVerticalBorder?: boolean;
  uuid: string;
  withExpandedIndicator?: boolean;
}

interface DataTableState {
  expandedRows: Map<string, boolean>;
  selectedRows: string[];
  sortedColumn?: {
    name: string;
    direction: SortOrder;
  };
}

const acceptedTypes = [
  ComponentType.LIST,
];

@AwesomeComponent({ type: ComponentType.LIST, api: DataTableAPI })
class DataTable extends Component<DataTableProps, DataTableState> {
  state: DataTableState = {
    expandedRows: new Map(),
    selectedRows: [],
  };

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: arrayOf(object).notEnumerable(),
    emptyResultSubtitleExpression: expression,
    emptyResultTitleExpression: expression,
    hiddenColumns: arrayOf(string).notEnumerable(),
    initialExpanded: bool,
    keyField: string,
    loading: bool,
    multiselect: bool,
    onClick: eventListener,
    onCollapse: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onDynamicContentRender: eventListener,
    onNextPage: eventListener,
    onPrevPage: eventListener,
    onSort: eventListener,
    showHorizontalBorder: bool,
    showVerticalBorder: bool,
    uuid: string,
    withExpandedIndicator: bool,
  };

  static defaultProps = {
    data: [],
    emptyResultTitleExpression: Expression.RESULTS_NOT_FOUND,
    hiddenColumns: [],
    multiselect: false,
    showHorizontalBorder: true,
    showVerticalBorder: true,
    withExpandedIndicator: true,
  };

  getVisibleColumns(): ColumnNode[] {
    const { children, hiddenColumns = DataTable.defaultProps.hiddenColumns } = this.props;

    return Children.toArray(children)
      .filter((child) => isDefined(child.props.visible) ? Boolean(child.props.visible) : true)
      .filter((child) => !hiddenColumns.includes(child.props.entry));
  }

  getColumnGroups(): ColumnGroups {
    const columns = this.getVisibleColumns();

    const barColumns = columns.filter(
      (column) => column.props.type === ColumnType.SUPERSCRIPTION_BAR
    );

    const defaultColumns = difference(columns, barColumns);

    return {
      barColumns,
      defaultColumns,
    };
  }

  getColumnsProps(columns: ColumnNode[] = []): Partial<DataTableColumnProps>[] {
    const { sortedColumn } = this.state;

    return columns.map((child) => ({
      ...child.props,
      focused: sortedColumn && sortedColumn.name === child.props.entry,
    }));
  }

  onCheckboxRowChange = (data: Record<string, any>): void => {
    const { keyField } = this.props;
    const { selectedRows } = this.state;

    const value = data[keyField];

    const newSelectedRows = selectedRows.includes(value)
      ? [...selectedRows].filter((row) => row !== value)
      : [...selectedRows, value];

    this.setState({ selectedRows: newSelectedRows });
  };

  onCheckboxColumnChange = (): void => {
    const { data, keyField } = this.props;
    const { selectedRows } = this.state;

    const newSelectedRows =
      selectedRows.length >= 0 && selectedRows.length != data.length
        ? data.map((item) => item[keyField])
        : [];

    this.setState({ selectedRows: newSelectedRows });
  };

  onColumnSort = (event: SortEvent): void => {
    const { name } = event;
    const { onSort } = this.props;

    const fn = (): void => {
      const { name = '', direction = '' } = this.state.sortedColumn || {};

      if (onSort && isFunction(onSort)) {
        onSort(new SortEvent({ name, direction }));
      }
    };

    this.setState((prevState) => {
      const { sortedColumn } = prevState;

      let direction = SortOrder.DESC;
      if (sortedColumn && sortedColumn.name === name) {
        direction = sortedColumn.direction === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC;
      }

      return {
        sortedColumn: { direction, name },
      };
    }, fn);
  };

  onDynamicContentRender = (event: RenderEvent): void => {
    const { onDynamicContentRender } = this.props;

    if (onDynamicContentRender && isFunction(onDynamicContentRender)) {
      onDynamicContentRender(event);
    }
  };

  onRowClick = (data: Record<string, any>): void => {
    const { initialExpanded, keyField, onCollapse } = this.props;
    const { expandedRows } = this.state;

    const key = data[keyField];

    const expanded = expandedRows.has(key) ? expandedRows.get(key) : Boolean(initialExpanded);
    expandedRows.set(key, !expanded);

    this.setState({ expandedRows });

    if (onCollapse && isFunction(onCollapse)) {
      onCollapse(new CollapseEvent());
    }
  };

  getClassName(): string {
    const { className, showHorizontalBorder, showVerticalBorder } = this.props;

    return classNames({
      'c-data-table': true,
      'c-data-table--with-vertical-border': Boolean(showVerticalBorder),
      'c-data-table--with-horizontal-border': Boolean(showHorizontalBorder),
      [`${className}`]: Boolean(className),
    });
  }

  renderLoader(): ReactElement | null {
    const { loading } = this.props;

    if (!loading) {
      return null;
    }

    return (
      <Loader className="c-data-table__loader" size={isMobile() ? 32 : 64} />
    );
  }

  renderMultiSelectColumn(): ReactElement | null {
    const { selectedRows } = this.state;
    const { data, multiselect } = this.props;

    if (!multiselect) {
      return null;
    }

    return (
      <DataTableMultiSelectColumn
        dataLength={data.length}
        name="data-table-column-checkbox"
        onChange={this.onCheckboxColumnChange}
        rowsLength={selectedRows.length}
      />
    );
  }

  renderSuperscriptionColumn(column: ColumnNode, index: number): ReactElement {
    const { showSuperscriptionLabel } = column.props;

    if (showSuperscriptionLabel) {
      return this.renderColumn(column, index);
    }

    return (
      <DataTableColumn
        className="c-data-table__superscription-bar-column"
        key={`c-data-table__column-${index}`}
        value=" "
        width={8}
      />
    );
  }

  renderColumn(column: ColumnNode, index: number): ReactElement {
    const { sortedColumn } = this.state;

    const inheritedProperties: Partial<DataTableColumnProps> = {
      key: `c-data-table__column-${index}`,
      onSort: this.onColumnSort,
    };

    if (sortedColumn && sortedColumn.name === column.props.entry) {
      inheritedProperties.focused = true;
      inheritedProperties.sortDirection = sortedColumn.direction;
    }

    return cloneElement(column, inheritedProperties);
  }

  renderColumns(): ReactElement {
    const { barColumns, defaultColumns } = this.getColumnGroups();

    return (
      <React.Fragment>
        {barColumns.map((child, index) => this.renderSuperscriptionColumn(child, index))}

        {this.renderMultiSelectColumn()}

        {defaultColumns.map((child, index) => this.renderColumn(child, index))}
      </React.Fragment>
    );
  }

  renderRow = (item: Record<string, any>, index: number, columnGroupsProps: ColumnGroupsProps, width: number): ReactElement => {
    const {
      initialExpanded,
      keyField,
      multiselect = DataTable.defaultProps.multiselect,
      withExpandedIndicator = DataTable.defaultProps.withExpandedIndicator,
    } = this.props;
    const { selectedRows, expandedRows } = this.state;

    const checked = multiselect && selectedRows.includes(item[keyField]);
    const expanded = expandedRows.has(item[keyField]) ? expandedRows.get(item[keyField]) : Boolean(initialExpanded);

    return (
      <DataTableRow
        checked={checked}
        collapsed={!expanded}
        columnGroups={columnGroupsProps}
        data={item}
        index={index}
        key={`data-table-row-${index}`}
        multiselect={multiselect}
        onChange={this.onCheckboxRowChange}
        onClick={this.onRowClick}
        onDynamicContentRender={this.onDynamicContentRender}
        width={width}
        withExpandedIndicator={withExpandedIndicator}
      />
    );
  };

  renderRows(width: number): ReactElement[] | null {
    const { data, loading } = this.props;

    if (loading || !Array.isArray(data)) {
      return null;
    }

    const groups = this.getColumnGroups();

    const columnGroupsProps: ColumnGroupsProps = {
      barColumns: this.getColumnsProps(groups.barColumns),
      defaultColumns: this.getColumnsProps(groups.defaultColumns),
    };

    return data.map((item, index) => this.renderRow(item, index, columnGroupsProps, width));
  }

  renderEmptyMessage(): ReactElement | null {
    const { data, emptyResultSubtitleExpression, emptyResultTitleExpression, loading } = this.props;

    if (data.length || loading) {
      return null;
    }

    return (
      <NotFound
        className="c-data-table__not-found"
        titleExpression={emptyResultTitleExpression}
        subtitleExpression={emptyResultSubtitleExpression}/>
    );
  }

  renderDataTable(width: number): ReactElement {
    return (
      <div className="c-data-table__wrapper">
        <div className="c-data-table__header" data-testid="data-table-header">{this.renderColumns()}</div>
        <div className="c-data-table__body">{this.renderRows(width)}</div>
      </div>
    );
  }

  render(): ReactElement | null {
    const { data, uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }

    return (
      <SizeMe>
        {({ size }) => (
          <Container
            acceptedTypes={acceptedTypes}
            className={this.getClassName()}
            uuid={uuid}
          >
            {!data.length ? this.renderEmptyMessage() : this.renderDataTable(size.width ?? 0)}
            {this.renderLoader()}
          </Container>
        )}
      </SizeMe>
    );
  }
}

export default DataTable;
